import { useRef, useState } from "react";
import { Upload, FileText, Image as ImageIcon, X, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/heic", "image/heif", "application/pdf"];
const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".heic", ".heif", ".pdf"];
const MAX = 10 * 1024 * 1024;

function isAllowedFile(f) {
 // Some mobile browsers (iOS Safari, Android Chrome with HEIC) leave f.type empty.
 // In that case, fall back to checking the filename extension.
 if (f.type && ALLOWED.includes(f.type.toLowerCase())) return true;
 if (!f.type) {
 const name = (f.name || "").toLowerCase();
 return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
 }
 return false;
}

function FileChip({ file, onRemove }) {
 const isImg = file.content_type?.startsWith("image/");
 return (
 <div data-testid={`uploaded-${file.id}`} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-paper px-3 py-2.5">
 <div className="w-9 h-9 rounded-lg bg-sage-500/15 text-sage-700 flex items-center justify-center shrink-0">
 {isImg ? <ImageIcon className="w-4 h-4" strokeWidth={1.75} /> : <FileText className="w-4 h-4" strokeWidth={1.75} />}
 </div>
 <div className="min-w-0 flex-1">
 <p className="text-sm font-medium text-ink truncate">{file.original_name}</p>
 <p className="text-xs text-muted_text">{(file.size / 1024).toFixed(0)} KB · uploaded</p>
 </div>
 <CheckCircle2 className="w-4 h-4 text-sage-600 shrink-0" />
 {onRemove && (
 <button type="button" onClick={() => onRemove(file)} aria-label="Remove" data-testid={`remove-${file.id}`} className="p-1 rounded-full hover:bg-cream">
 <X className="w-4 h-4 text-muted_text" />
 </button>
 )}
 </div>
 );
}

export default function FileUploader({ kind = "other", label, hint, multiple = false, value = [], onChange, testId }) {
 const inputRef = useRef(null);
 const [busy, setBusy] = useState(false);

 async function handleFiles(filesList) {
 const files = Array.from(filesList || []);
 if (!files.length) return;
 if (!multiple && files.length > 1) files.splice(1);
 setBusy(true);
 const next = [...value];
 for (const f of files) {
 if (!isAllowedFile(f)) {
 toast.error(`${f.name}: unsupported type. Use PNG, JPG, WEBP, HEIC, or PDF.`);
 continue;
 }
 if (f.size > MAX) { toast.error(`${f.name}: too large (max 10MB).`); continue; }
 try {
 const fd = new FormData();
 fd.append("file", f);
 fd.append("kind", kind);
 // IMPORTANT: do NOT set Content-Type manually here. Axios/fetch must
 // generate the multipart boundary itself, otherwise the server will
 // not be able to parse the form.
 const { data } = await api.post("/uploads", fd);
 if (multiple) next.push(data); else { next.length = 0; next.push(data); }
 } catch (err) {
 const detail = err?.response?.data?.detail;
 const status = err?.response?.status;
 const human = formatApiError(detail) || (status ? `Upload failed (${status}) for ${f.name}` : `Upload failed for ${f.name}. Check your connection and try again.`);
 console.error("Upload failed", { name: f.name, type: f.type, size: f.size, status, detail });
 toast.error(human);
 }
 }
 onChange && onChange(next);
 setBusy(false);
 if (inputRef.current) inputRef.current.value = "";
 }

 function remove(file) {
 onChange && onChange(value.filter((v) => v.id !== file.id));
 }

 return (
 <div data-testid={testId}>
 {label && <p className="text-sm font-medium text-ink mb-1">{label}</p>}
 {hint && <p className="text-xs text-muted_text mb-3">{hint}</p>}

 {(value.length === 0 || multiple) && (
 <button
 type="button"
 onClick={() => inputRef.current?.click()}
 disabled={busy}
 data-testid={`${testId || "uploader"}-trigger`}
 className="w-full rounded-2xl border-2 border-dashed border-sage-300/60 hover:border-sage-700 hover:bg-sage-50/60 transition-all p-5 flex items-center gap-4 text-left disabled:opacity-60"
 >
 <span className="w-11 h-11 rounded-xl bg-sage-50 text-sage-700 flex items-center justify-center">
 {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" strokeWidth={1.75} />}
 </span>
 <span className="flex-1 min-w-0">
 <span className="block text-sm font-medium text-ink">{busy ? "Uploading..." : (value.length === 0 ? "Click to upload" : "Add another")}</span>
 <span className="block text-xs text-muted_text">PNG, JPG, WEBP, HEIC or PDF · up to 10MB</span>
 </span>
 </button>
 )}

 <input ref={inputRef} type="file" hidden multiple={multiple} onChange={(e) => handleFiles(e.target.files)} accept={ALLOWED.join(",")} />

 {value.length > 0 && (
 <div className="mt-3 grid gap-2">
 {value.map((f) => (<FileChip key={f.id} file={f} onRemove={() => remove(f)} />))}
 </div>
 )}
 </div>
 );
}
